import { useEffect, useState } from 'react'

import cl from 'classnames'
import { Container, Paper, Typography } from '@material-ui/core'
import { styled } from '@material-ui/core/styles'
import Rating from '@material-ui/lab/Rating'

import theme from 'artkive/utils/theme'

const RADIUS = 8

// Polaris turns its `layout` words into modifiers of the widget.
export const LAYOUT_CLASSES = {
  bordered: 'ElementsWidget--bordered',
  large: 'ElementsWidget--spacing-lg',
  reverse: 'ElementsWidget--reverse',
}

// Polaris marks the widget with every class whose width the widget is at or under.
const BREAKPOINTS = { 'R-XSM': 380, 'R-SM': 480, 'R-MD': 640, 'R-LG': 767, 'R-XLG': 992 }

export const useBreakpointClasses = (ref) => {
  const [classes, setClasses] = useState('')

  useEffect(() => {
    const observer = new ResizeObserver(([entry]) => {
      const { width } = entry.contentRect
      setClasses(cl(Object.keys(BREAKPOINTS).filter((name) => width <= BREAKPOINTS[name])))
    })
    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [ref])

  return classes
}

export const PageRoot = styled('div')({
  backgroundColor: theme.colors.white.dark,
  minHeight: '100vh',
  overflowX: 'hidden',
})

export const Hero = styled('section')({
  backgroundColor: theme.colors.purple.med,
})

// Extra bottom room so the summary card can overlap the band, as the Contact page card does.
export const HeroContainer = styled(Container)(({ theme: { breakpoints, spacing } }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: spacing(6, 2, 14),
  [breakpoints.up('md')]: {
    padding: spacing(10, 3, 18),
  },
}))

export const SummaryCard = styled(Paper)(({ theme: { breakpoints, spacing } }) => ({
  position: 'relative',
  marginTop: -spacing(9),
  padding: spacing(4, 2),
  textAlign: 'center',
  backgroundColor: theme.colors.white.main,
  boxShadow: theme.shadows[7],
  borderRadius: RADIUS,
  [breakpoints.up('md')]: {
    marginTop: -spacing(12),
    padding: spacing(6, 8),
  },
}))

export const RatingRow = styled('div')(({ theme: { spacing } }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexWrap: 'wrap',
  gap: spacing(1.5),
  marginBottom: spacing(1.5),
}))

export const StarRating = styled(Rating)({
  fontSize: '1.375rem',
  verticalAlign: 'middle',
  '& .MuiRating-iconFilled': {
    color: theme.colors.yellow.dark,
  },
})

export const Cta = styled(Typography)(({ theme: { spacing } }) => ({
  fontWeight: 600,
  marginBottom: spacing(3),
}))

// Restyles the third-party REVIEWS.io widget markup, which only exposes its own class names.
export const ReviewsWrapper = styled('div')(({ theme: { spacing } }) => ({
  '& .R-ContentList__item': {
    backgroundColor: theme.colors.white.main,
    boxShadow: theme.shadows[7],
    borderRadius: RADIUS,
    border: 'none',
  },
  '& .c-meta__authorDetails .c-meta__authorName': {
    marginTop: 4,
  },
  '& .subheader__group--filters': {
    flex: '1 1 auto',
  },
  '& .c-searchFilters': {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing(2),
    width: '100%',
    maxWidth: 560,
  },
  '& .c-searchFilters__filters': {
    flex: '0 0 auto',
  },
  '& .c-searchFilters__search': {
    flex: '1 1 240px',
  },
  '& .c-searchFilters__search .ElementsWidget__search': {
    width: '100%',
  },
  '& .c-searchFilters__search .R-Field, & .c-searchFilters__search .R-Field__input': {
    maxWidth: 'none',
    width: '100%',
  },
  // The list only carries company reviews, so the widget's one-tab "Company Reviews" switcher has nothing to switch.
  '& .subheader__group--tabs': {
    display: 'none',
  },
  // No filters are enabled, so the widget's "Clear Filters" link and its divider are dead weight.
  '& .c-filters__dropdown__clearButton, & .c-filters__dropdown__clearButton + .u-hr': {
    display: 'none',
  },
}))

export const HighlightsRoot = styled('div')(({ theme: { spacing } }) => ({
  marginTop: spacing(3),
}))

export const Basis = styled('p')({
  color: theme.colors.grey[700],
})

export const HighlightsText = styled(Typography)(({ theme: { breakpoints, spacing } }) => ({
  maxWidth: 760,
  margin: spacing(2, 'auto', 0),
  textAlign: 'left',
  [breakpoints.up('md')]: {
    textAlign: 'center',
  },
}))

export const ThemeList = styled('ul')(({ theme: { spacing } }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: spacing(1),
  listStyle: 'none',
  margin: spacing(3, 0, 0),
  padding: 0,
}))

export const ThemeChip = styled('li')(({ theme: { spacing } }) => ({
  padding: spacing(0.5, 1.5),
  borderRadius: 16,
  backgroundColor: theme.colors.bg.sky,
  color: theme.colors.black,
  fontSize: '0.8125rem',
  fontWeight: 600,
}))

export const ThemeCount = styled('span')({
  color: theme.colors.grey[700],
  fontWeight: 500,
})

export const QuoteList = styled('ul')(({ theme: { spacing } }) => ({
  listStyle: 'none',
  padding: 0,
  marginTop: spacing(3),
  textAlign: 'left',
}))

export const Quote = styled('blockquote')(({ theme: { spacing } }) => ({
  margin: 0,
  padding: spacing(1.5, 2),
  height: '100%',
  borderLeft: `3px solid ${theme.colors.green.main}`,
  borderRadius: `0 ${RADIUS}px ${RADIUS}px 0`,
  backgroundColor: theme.colors.green.light,
}))

// The one quote that carries a reservation, so the block reads as balanced rather than all praise.
export const CriticalQuote = styled(Quote)({
  borderLeftColor: theme.colors.yellow.dark,
  backgroundColor: theme.colors.yellow.light,
})

export const QuoteMeta = styled('footer')(({ theme: { spacing } }) => ({
  display: 'block',
  marginTop: spacing(1),
  color: theme.colors.grey[700],
}))

export const QuoteAuthor = styled('cite')({
  fontStyle: 'normal',
  fontWeight: 600,
  color: theme.colors.black,
})

export const Counts = styled('p')(({ theme: { palette, spacing } }) => ({
  marginTop: spacing(3),
  color: palette.primary.main,
  fontWeight: 600,
}))
